import React from "react";
import ProgressBarStyles from "./ProgressBar.module.scss";
import { recommendationsColors } from "../../modules/vscDashboard/constants";

interface SegmentedCircularProgressProps {
  progressPercent: number;
  dataLength?: number;
}

const SegmentedCircularProgress = ({
  progressPercent,
  dataLength = 0,
}: SegmentedCircularProgressProps) => {
  const normalizedPercent = isNaN(progressPercent)
    ? 0
    : Number(progressPercent);

  // If no data, render text inside a single segmented circle
  if (dataLength === 0) {
    return (
      <div className={ProgressBarStyles.progress}>
        <svg viewBox="0 0 100 100" className={ProgressBarStyles.progressSvg}>
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="transparent"
            stroke="#9aa0a6"
            strokeWidth="10"
          />
          {/* <circle cx="50" cy="50" r="30" fill="white" /> */}
        </svg>
        <div className={ProgressBarStyles.noDataContent}>
          <span className={ProgressBarStyles.text}>
            Contracts with those recommendations
          </span>
        </div>
      </div>
    );
  }

  // Use dataLength to determine the number of segments, capped at recommendationsColors.length
  const segments = Math.min(dataLength, recommendationsColors.length);

  // Calculate the circumference of the circle (2πr)
  const radius = 40;
  const circumference = 2 * Math.PI * radius;

  // Add a small gap between segments (unless there's only 1 segment)
  const gapSize = segments === 1 ? 0 : 3; // Size of gap in degrees
  const totalGapDegrees = gapSize * segments;
  const remainingDegrees = 360 - totalGapDegrees;
  const segmentDegrees = remainingDegrees / segments;

  // Convert from degrees to stroke-dasharray length
  const segmentLength = (segmentDegrees / 360) * circumference;
  const gapLength = (gapSize / 360) * circumference;

  // Calculate how many segments should be filled based on percentage
  const filledSegmentsCount = Math.ceil((normalizedPercent / 100) * segments);

  return (
    <div className={ProgressBarStyles.progress}>
      <svg viewBox="0 0 100 100" className={ProgressBarStyles.progressSvg}>
        {/* Create a circular track with separate background segments */}
        {Array.from({ length: segments }).map((_, index) => {
          // For anti-clockwise rotation starting from top, we need to use negative angle
          const startAngle = -90 - (segmentDegrees + gapSize);
          const rotationAngle = startAngle - index * (segmentDegrees + gapSize);

          // Special case for single segment - no need for dasharray, make it a full circle
          const segmentProps =
            segments === 1
              ? {
                  strokeDasharray: "none",
                  strokeDashoffset: 0,
                }
              : {
                  strokeDasharray: `${segmentLength} ${
                    circumference - segmentLength
                  }`,
                  strokeDashoffset: 0,
                };

          return (
            <circle
              key={`bg-segment-${index}`}
              cx="50"
              cy="50"
              r={radius}
              fill="transparent"
              stroke="#d6d6d6"
              strokeWidth="10"
              {...segmentProps}
              transform={`rotate(${rotationAngle}, 50, 50)`}
            />
          );
        })}

        {/* Create the progress segments */}
        {Array.from({ length: segments }).map((_, index) => {
          // For anti-clockwise rotation starting from top, we need to use negative angle
          const startAngle = -90; // Start from top
          const rotationAngle = startAngle - index * (segmentDegrees + gapSize);

          // For our use case, we'll always fill all segments with their respective colors
          const isActive = true; // We want to show all segments with their colors

          // Special case for single segment - no need for dasharray, make it a full circle
          const segmentProps =
            segments === 1
              ? {
                  strokeDasharray: "none",
                  strokeDashoffset: 0,
                }
              : {
                  strokeDasharray: `${segmentLength} ${
                    circumference - segmentLength
                  }`,
                  strokeDashoffset: 0,
                };

          if (isActive) {
            return (
              <circle
                key={`segment-${index}`}
                cx="50"
                cy="50"
                r={radius}
                fill="transparent"
                stroke={recommendationsColors[index]} // Use the color from the array directly
                strokeWidth="10"
                {...segmentProps}
                transform={`rotate(${rotationAngle}, 50, 50)`}
              />
            );
          }
          return null;
        })}

        {/* Create the center white circle */}
        {/* <circle cx="50" cy="50" r="30" fill="white" /> */}
      </svg>

      {/* Text content - with more space from the circle edge */}
      <div className={ProgressBarStyles.content}>
        <span className={ProgressBarStyles["progress-percent"]}>
          {normalizedPercent.toFixed(2)}%
        </span>
        <span className={ProgressBarStyles.text}>Contracts Reviewed</span>
      </div>
    </div>
  );
};

export default SegmentedCircularProgress;
